import ChartDataLabels from "chartjs-plugin-datalabels";
import { useMemo } from "react";
import { Bar } from "react-chartjs-2";
import "./RatingChart.css"; // for styling
import { useIntl } from "react-intl";

import { SalesDashboardData } from "../../../data-store";
import {
  allImpactRatings,
  useImpactRatingToLongName,
  useImpactRatingToLetter,
  ImpactRating,
} from "../../../domain/impactRatings";
import {
  chartGridLineCol,
  highD,
  lowB,
  mediumC,
  midGreyCol,
  veryHighE,
  veryLowA,
} from "../../graphs/colors";
import { useImpactCategory } from "../ImpactCategoryProvider";

const DATA_MAP = {
  GHG: "ghg",
  WATER_USE: "waterUse",
  LAND_USE: "landUse",
};

const ratingToId = (rating: ImpactRating): string => {
  switch (rating) {
    case "VLOW":
      return "veryLow";
    case "LOW":
      return "low";
    case "MEDIUM":
      return "medium";
    case "HIGH":
      return "high";
    case "VHIGH":
      return "veryHigh";
  }
};

const RatingDistributionChart = ({ data }: { data: SalesDashboardData }) => {
  const intl = useIntl();
  const { impactCategory } = useImpactCategory();
  const getLongName = useImpactRatingToLongName;
  const getLetter = useImpactRatingToLetter;

  const labels = useMemo(
    () =>
      allImpactRatings.map((rating) => ({
        title: getLongName(rating),
        id: ratingToId(rating),
      })),
    [getLongName]
  );

  const badges = useMemo(() => allImpactRatings.map(getLetter), [getLetter]);

  const chartData = useMemo(() => {
    const salesData = labels.map((label) => {
      return (data.salesRatings as any)?.[DATA_MAP[impactCategory]][
        label.id
      ].toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    });

    const productsData = labels.map((label) => {
      return (data.productsRatings as any)[DATA_MAP[impactCategory]][
        label.id
      ].toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    });

    return {
      labels: labels.map((label) => label.title),
      datasets: [
        {
          label: intl.formatMessage({
            id: "components/pages:SalesDashBoard/RatingDistributionBarChart/sales",
            defaultMessage: "Sales",
          }),
          data: salesData,
          backgroundColor: [veryLowA, lowB, mediumC, highD, veryHighE],
        },
        {
          label: intl.formatMessage({
            id: "components/pages:SalesDashBoard/RatingDistributionBarChart/products",
            defaultMessage: "Products",
          }),
          data: productsData,
          backgroundColor: [veryLowA, lowB, mediumC, highD, veryHighE],
        },
      ],
    };
  }, [data.productsRatings, data.salesRatings, impactCategory, intl, labels]);

  const options = {
    indexAxis: "y" as const,
    responsive: true,
    maintainAspectRatio: false,
    borderRadius: 4,
    barPercentage: 0.8,
    maxBarThickness: 24,
    plugins: {
      legend: {
        display: false,
      },
      datalabels: {
        anchor: "end",
        align: "start",
        formatter: (value: number) => `${value}%`,
        color: "#fff",
        font: {
          size: 13,
          weight: "bold",
        },
      },
      tooltip: {
        callbacks: {
          label(context: any) {
            const label = context.dataset.label || "";
            const value = context.raw;
            return `${label}: ${value}%`;
          },
        },
      },
    },
    scales: {
      y: {
        display: false, // hide axis labels. We’ll render custom ones in HTML
      },
      x: {
        border: {
          display: false,
        },
        grid: {
          color: chartGridLineCol,
        },
        beginAtZero: true,
        ticks: {
          callback: (val: number | string) => `${val}%`,
          stepSize: 10,
          color: midGreyCol,
          font: {
            size: 13,
          },
        },
      },
    },
  };

  return (
    <div className="RatingDistributionBarChart_container">
      <h4 className="RatingDistributionBarChart_title">
        {intl.formatMessage({
          id: "components/pages:SalesDashBoard/RatingDistributionBarChart/title",
          defaultMessage: "Rating Distribution of Sales & Products",
        })}
      </h4>
      <div className="rating-chart-wrapper">
        <div
          className="chart-labels"
          style={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          {labels.map((label, i) => (
            <div
              className="d-flex"
              style={{
                justifyContent: "end",
                gap: 8,
              }}
              key={label.id}
            >
              <div
                className="group-label"
                style={{
                  gridRow: `span 2`,
                  justifyContent: "end",
                }}
              >
                <span className="SalesDashboardPage_RatingBarChart_label-rating">
                  {label.title}
                </span>
                <span className={`badge badge-${badges[i]}`}>{badges[i]}</span>
              </div>
              <div className="d-flex" style={{ flexDirection: "column" }}>
                <div className="sub-label">
                  {intl.formatMessage({
                    id: "components/pages:SalesDashBoard/RatingDistributionBarChart/sales",
                    defaultMessage: "Sales",
                  })}
                </div>
                <div className="sub-label">
                  {intl.formatMessage({
                    id: "components/pages:SalesDashBoard/RatingDistributionBarChart/products",
                    defaultMessage: "Products",
                  })}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="RatingDistributionBarChart_chart-container">
          <Bar
            data={chartData}
            options={options as any}
            height={labels.length * 60}
            width={"100%"}
            plugins={[ChartDataLabels]}
          />
        </div>
      </div>
    </div>
  );
};

export default RatingDistributionChart;
